import "./IsAtHomeStep.scss";

import React, {useEffect, useRef, useState} from "react";
import {
    IonButton,
    IonContent,
    IonLabel,
    IonRadio,
    IonRadioGroup,
    IonItem,
    IonSpinner, IonIcon
} from "@ionic/react";
import {MainFooter, MainHeader} from "../../Layout/Layout";
import {useHistory} from "react-router-dom";
import {useStateMachine} from "little-state-machine";
import {
    updateIsAtHome,
    updateTypedAddress,
    updateRdvPoint,
} from "../../../state/updateState";
import {Loader} from "@googlemaps/js-api-loader";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { Libraries } from "@react-google-maps/api/dist/utils/make-load-script-url";
import {useTranslation} from "react-i18next";
import {RoutePath} from "../../../model/RoutePath.enum";
import {Api} from "../../../api/api";
import countryCodes from "../../../assets/utils/country-code";
import Loading from "../../Loading/Loading";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY!;
const libraries: Libraries = ["places"];

const IsAtHomeStep: React.FC = () => {
    const {t} = useTranslation();
    const {state, actions} = useStateMachine({
        updateIsAtHome,
        updateTypedAddress,
        updateRdvPoint,
    });
    const history = useHistory();

    const [isAtHome, setIsAtHome] = useState<boolean | null>(state.isAtHome ?? null);
    const [address, setAddress] = useState(state.typedAddress ?? "");
    const [rdvPoint, setRdvPoint] = useState<google.maps.LatLngLiteral | null>(state.rdvPoint ?? null);
    const [loading, setLoading] = useState<boolean>(false);
    const [salesforceAddress, setSalesforceAddress] = useState<string | undefined>(undefined);
    const [typedInput, setTypedInput] = useState<string>("");

    const autocompleteRef = useRef<HTMLInputElement>(null);
    const [autoComplete, setAutoComplete] = useState<google.maps.places.Autocomplete | null>(null);
    const [placeName, setPlaceName] = useState<string>("");

    const { isLoaded, loadError } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    });
    
    useEffect(() => {
        if (state.rdvPoint) {
            setRdvPoint(state.rdvPoint);
        }
        if (state.typedAddress) {
            setAddress(state.typedAddress);
            setTypedInput(state.typedAddress.trim());
        }
    }, []);

    // Update input field value when address changes
    useEffect(() => {
        if (autocompleteRef.current && address) {
            autocompleteRef.current.value = address;
        }
    }, [address]);

    // Ensure input is populated when the autocomplete input becomes available
    useEffect(() => {
        if (isLoaded && isAtHome !== null && autocompleteRef.current && address) {
            autocompleteRef.current.value = address;
        }
    }, [isLoaded, isAtHome]);

    useEffect(() => {
        // When Google Maps is loaded and user has selected "Yes", retrieve address
        if (isLoaded && isAtHome === true && !salesforceAddress && !address) {
            retrievePersonalAddress();
        }
    }, [isLoaded, isAtHome]);

    const searchPlace = () => {
        if (autoComplete) {
            const place = autoComplete.getPlace();
            if (place && place.geometry && place.geometry.location) {
                const position = {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                };
                
                setRdvPoint(position);
                
                if (place.name) {
                    setPlaceName(place.name);
                }
                
                if (place.formatted_address) {
                    setAddress(place.formatted_address);
                }
            } else {
                searchGeocodingManually();
            }
        } else {
            searchGeocodingManually();
        }
    };

    const searchGeocodingManually = (): Promise<{location: google.maps.LatLngLiteral, address: string} | null> => {
        return new Promise((resolve) => {
            if (autocompleteRef.current && autocompleteRef.current.value) {
                const inputValue = autocompleteRef.current.value;
                const geocoder = new google.maps.Geocoder();

                // Fallback: try direct geocoding with the typed address
                const tryDirectGeocoding = () => {
                    geocoder.geocode(
                        { address: inputValue },
                        function (results, status) {
                            if (status === "OK" && results && results[0]) {
                                const location = {
                                    lat: results[0].geometry.location.lat(),
                                    lng: results[0].geometry.location.lng(),
                                };
                                const formattedAddress = results[0].formatted_address;
                                setRdvPoint(location);
                                setAddress(formattedAddress);

                                if (autocompleteRef.current) {
                                    autocompleteRef.current.value = formattedAddress;
                                }
                                resolve({ location, address: formattedAddress });
                            } else {
                                console.log("Geocode was not successful for the following reason: " + status);
                                resolve(null);
                            }
                        }
                    );
                };

                // First, try to get autocomplete predictions (more accurate than direct geocoding)
                const autocompleteService = new google.maps.places.AutocompleteService();
                autocompleteService.getPlacePredictions(
                    { input: inputValue },
                    (predictions, predStatus) => {
                        if (predStatus === "OK" && predictions && predictions.length > 0) {
                            // Use the first prediction
                            const firstPrediction = predictions[0];

                            // Geocode the first prediction's description
                            geocoder.geocode(
                                { address: firstPrediction.description },
                                (predResults, predGeoStatus) => {
                                    if (predGeoStatus === "OK" && predResults && predResults[0]) {
                                        const location = {
                                            lat: predResults[0].geometry.location.lat(),
                                            lng: predResults[0].geometry.location.lng(),
                                        };
                                        const formattedAddress = predResults[0].formatted_address;
                                        setRdvPoint(location);
                                        setAddress(formattedAddress);

                                        if (autocompleteRef.current) {
                                            autocompleteRef.current.value = formattedAddress;
                                        }
                                        resolve({ location, address: formattedAddress });
                                    } else {
                                        console.log("Prediction geocoding failed, trying direct geocoding...");
                                        tryDirectGeocoding();
                                    }
                                }
                            );
                        } else {
                            console.log("No autocomplete predictions found, trying direct geocoding...");
                            tryDirectGeocoding();
                        }
                    }
                );
            } else {
                resolve(null);
            }
        });
    };

    const onLoadAuto = (autocomplete: google.maps.places.Autocomplete) => {
        setAutoComplete(autocomplete);

        // Set the input value if address exists (e.g., when navigating back)
        if (autocompleteRef.current && address) {
            autocompleteRef.current.value = address;
        }
    };

    const handleGeolocate = () => {
        setLoading(true);

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const loc = {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                };

                setRdvPoint(loc);
                setAddress(""); // Not typed
                setLoading(false);

                actions.updateTypedAddress(undefined);
                actions.updateIsAtHome(false);
                actions.updateRdvPoint(loc);

                history.push(`${RoutePath.REQUEST}/${RoutePath.CONFIRM_RDV_POINT}`);
            },
            (err) => {
                console.warn("Geolocation failed:", err);
                setLoading(false);
                history.push(`${RoutePath.REQUEST}/${RoutePath.MANUAL_LOCATION}`);
            },
            {enableHighAccuracy: true, timeout: 5000}
        );
    };

    async function handleNext() {
        // Check if user has typed something in the address bar but didn't use autocomplete
        if (autocompleteRef.current && autocompleteRef.current.value && !rdvPoint) {
            const geocodedResult = await searchGeocodingManually();

            if (!geocodedResult) {
                alert(t("Unable to find the address you entered. Please review and try again."));
                return;
            }

            // Proceed immediately with the geocoded location and address
            proceedToNext(geocodedResult.location, geocodedResult.address);
        } else {
            proceedToNext();
        }
    }

    function proceedToNext(locationOverride?: google.maps.LatLngLiteral, addressOverride?: string) {
        const locationToUse = locationOverride || rdvPoint;
        const addressToUse = addressOverride || address;

        if (!locationToUse && !isAtHome) return;

        actions.updateTypedAddress(addressToUse);
        actions.updateIsAtHome(isAtHome);

        if (locationToUse) {
            actions.updateRdvPoint(locationToUse);
        }

        if (isAtHome || locationToUse) {
            history.push(`${RoutePath.REQUEST}/${RoutePath.CONFIRM_RDV_POINT}`);
        } else {
            history.push(`${RoutePath.REQUEST}/${RoutePath.MANUAL_LOCATION}`);
        }
    }

    const retrievePersonalAddress = () => {
        const phoneNumber = state.cachedPasswords?.phoneNumber;
        const accessToken = state.token?.access_token;

        const shouldUseSalesforce =
            process.env.REACT_APP_USE_SALESFORCE_ADDRESS === "true";

        // 🚫 Combined guard clause
        if (
            !phoneNumber ||
            !accessToken ||
            salesforceAddress ||
            !isAtHome ||
            address !== "" ||
            !shouldUseSalesforce
        ) {
            return;
        }

        setLoading(true);

        Api.getPersonalInfo(phoneNumber, accessToken)
            .then((resp) => {
                const rawAddress = resp.data?.[0]?.legalAddresses?.[0];

                if (!rawAddress?.country) return;

                const countryName =
                    countryCodes[rawAddress.country as keyof typeof countryCodes];

                if (!countryName) return;

                const userSalesforceAddress = [
                    rawAddress.streetName,
                    [rawAddress.houseNumber, rawAddress.cptHouseNumber]
                        .filter(Boolean)
                        .join(""),
                    [rawAddress.npa, rawAddress.locality].filter(Boolean).join(" "),
                    countryName,
                ]
                    .filter(Boolean)
                    .join(", ")
                    .trim();

                if (userSalesforceAddress) {
                    setSalesforceAddress(userSalesforceAddress);

                    const locationInput = autocompleteRef.current;
                    if (locationInput) {
                        locationInput.value = userSalesforceAddress;
                        setAddress(userSalesforceAddress);
                        setTypedInput(userSalesforceAddress.trim());

                        // Use searchPlace logic to geocode the address
                        if (isLoaded) {
                            const geocoder = new google.maps.Geocoder();
                            geocoder.geocode({ address: userSalesforceAddress }, (results, status) => {
                                if (
                                    status === "OK" &&
                                    results &&
                                    results.length > 0 &&
                                    results[0].geometry?.location
                                ) {
                                    const loc = results[0].geometry.location;
                                    setAddress(results[0].formatted_address || userSalesforceAddress);
                                    setRdvPoint({
                                        lat: loc.lat(),
                                        lng: loc.lng(),
                                    });
                                } else {
                                    console.warn("Manual geocoding failed:", status);
                                }
                            });
                        }
                    }
                }
            })
            .catch((error) => {
                console.error("Failed to retrieve address from Salesforce:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    console.log("rdvPoint:", rdvPoint, "address:", address);
    return (
        <IonContent>
            <MainHeader
                title={t("Is your vehicle currently parked at your home?")}
                image={`${process.env.PUBLIC_URL}/assets/images/${
                    process.env.REACT_APP_ALT_ICONS_PATH || ""
                }Home.svg`}
            >
                <section>
                    <IonRadioGroup
                        className="parked-home-vehicle-question"
                        name="parkedAtHome" // should have the same name as the form
                        onIonChange={(e) => {
                            const value = e.detail.value;
                            setIsAtHome(value);
                            
                            if (value === true) {
                                // When "Yes" is selected, retrieve personal address
                                if (isLoaded) {
                                    retrievePersonalAddress();
                                }
                            } else {
                                // When "No" is selected, clear the address
                                if (autocompleteRef.current) {
                                    autocompleteRef.current.value = "";
                                }
                                setAddress("");
                                setRdvPoint(null);
                                setTypedInput("");
                            }
                        }}
                        value={isAtHome}
                    >
                        <IonItem key={"yes"} className="options radio-as-button">
                            <span className={"tab"}>{t("Yes")}</span>
                            <IonRadio slot="end" value={true}/>
                        </IonItem>
                        <IonItem key={"no"} className="options radio-as-button">
                            <span className={"tab"}>{t("No")}</span>
                            <IonRadio slot="end" value={false}/>
                        </IonItem>
                    </IonRadioGroup>
                </section>


                {/* NOT AT HOME SELECTED */}
                {isAtHome === false && (
                    <>
                        <div className="not-home-section">
                            <h2>{t("Provide your GPS location")}</h2>
                            <IonButton
                                color="primary"
                                className="button-tcs locate-me-now-button cta"
                                expand="block"
                                onClick={handleGeolocate}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <IonSpinner name="dots"/>
                                        &nbsp; Locating...
                                    </>
                                ) : (
                                    <>
                                        {t("Locate me now")}
                                    </>
                                )}
                            </IonButton>
                        </div>
                        <div className="divider-or">
                            <span>{t("Or")}</span>
                        </div>
                    </>
                )}
                {isAtHome !== null && isLoaded && !loadError && (
                    <div className="autocomplete-container">
                        <div className="title-container">
                            <h2 className="layout-title">
                                {isAtHome === true && (
                                    <>{t("Please confirm your location. You can edit it if it is incorrect")}</>
                                )}
                                {isAtHome === false && (
                                    <>{t("Enter an address or a point of interest near your location")}</>
                                )}
                            </h2>
                        </div>
                        <Autocomplete
                            onLoad={onLoadAuto}
                            fields={[
                                "geometry",
                                "formatted_address",
                                "address_components",
                                "name",
                            ]}
                            className="autocomplete"
                            onPlaceChanged={searchPlace}
                        >
                            <input
                                ref={autocompleteRef}
                                defaultValue={address}
                                placeholder={isAtHome
                                    ? t("Type your current address at home")
                                    : t("Type your current address not at home")
                                }
                                className="autocomplete-input"
                                onChange={(e) => {
                                    const value = e.target.value.trim();
                                    setTypedInput(value);

                                    // Clear rdvPoint when user modifies the address
                                    // This ensures we geocode again if they change it
                                    if (rdvPoint) {
                                        setRdvPoint(null);
                                    }

                                    if (!value) {
                                        setAddress("");
                                    }
                                }}
                            />
                        </Autocomplete>
                    </div>
                )}
            </MainHeader>
            <MainFooter>
                <IonButton
                    expand="block"
                    className="button-tcs back"
                    onClick={() => history.back()}
                >
                    <div className="wrapper">
                        <IonIcon
                            size="large"
                            src={`${process.env.PUBLIC_URL}/assets/images/chevron-back.svg`}
                        />
                        <IonLabel>{t("Back")}</IonLabel>
                        <div/>
                    </div>
                </IonButton>
                <IonButton
                    expand="block"
                    className="button-tcs cta"
                    color="secondary"
                    onClick={handleNext}
                    disabled={isAtHome === null || (!rdvPoint && !typedInput)}
                >
                    <div className="wrapper">
                        <div/>
                        <IonLabel>{t("Next")}</IonLabel>
                        <IonIcon
                            size="large"
                            src={`${process.env.PUBLIC_URL}/assets/images/chevron-forward.svg`}
                        />
                    </div>
                </IonButton>
            </MainFooter>
            {loading && <Loading/>}
        </IonContent>
    );
};

export default IsAtHomeStep;
